import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Redirect } from 'react-router';
import {
    Grid, Card, CardContent, CardHeader, IconButton, Button, Typography,
    FormControl, TextField, Box, Modal, Divider, CardActions,
    Autocomplete, Backdrop, CircularProgress, Menu, MenuItem
} from '@mui/material';
import { withTranslation, WithTranslation } from 'react-i18next';
import ReportsHeader from './ReportsHeader';
import {
    DataGrid, GridCellParams
} from '@mui/x-data-grid';
import { CloseOutlined, SaveAlt as SaveAltIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatDate } from '../../helpers/custom';
import axios from '../../helpers/axios';
import { isUatEnvironment } from '../../helpers/uatMasking';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '65%',
    p: 4,
};

function CustomToolbar({ onExport }: { onExport: () => void }) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const handleCsv = () => {
        handleClose();
        onExport();
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
                variant='contained'
                size='small'
                startIcon={<SaveAltIcon />}
                onClick={handleClick}
                sx={{ outline: 'none !important', textTransform: 'uppercase', fontSize: '0.8125rem', fontWeight: 500 }}
            >
                Export
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleCsv}>Download as CSV</MenuItem>
            </Menu>
        </Box>
    );
}

export interface IProps {
    user: {
        id: number;
        name: string;
        email: string;
        role: string;
        is_active: boolean;
        selectedHierarchies: any[];
        selectedHierarchyIds: any[];
    };
}

export interface IState {
    showLoader: boolean;
    communities: { id: number; name: string; type: string }[];
    community: any;
    fromDate?: string | null;
    toDate?: string | null;
    pageSize: any;
    rows: any[];
    showImageModal: boolean;
    currentImage: string;
    currentPage: number;
    rowCount: number;
}

class OddBagReport extends React.Component<RouteComponentProps<any> & IProps & WithTranslation, IState> {
    constructor(props: RouteComponentProps & IProps & WithTranslation) {
        super(props);
        this.state = {
            showLoader: false,
            communities: [{ id: 0, name: 'All', type: 'City' }],
            community: '',
            fromDate: null,
            toDate: null,
            pageSize: 10,
            rows: [],
            showImageModal: false,
            currentImage: '',
            currentPage: 0,
            rowCount: 0,
        };
    }

    public componentDidMount(): void {
        this.fetchCommunities();
        this.handleFetchReport();
    }

    public fetchCommunities() {
        axios.get('accessibleHierarchiesList')
            .then((response) => {
                if (response.data.success) {
                    const selectedHierarchyIds = this.props.user.selectedHierarchyIds || [];
                    const communities = [
                        { id: 0, name: 'All', type: 'City' },
                        ...(response.data.data || [])
                            .filter((city: any) => selectedHierarchyIds.includes(city.id))
                            .map((city: any) => ({
                            id: city.id,
                            name: city.name,
                            type: city.type || 'City',
                        })),
                    ];
                    this.setState({ communities });
                }
            })
            .catch((error) => {
                console.error('Error fetching cities:', error);
            });
    }

    handleExportCsv = () => {
        const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const buildImageHyperlink = (url: string, barcode: string, index: number) => {
            const label = `${barcode || 'Image'}_${index + 1}`;
            return `=HYPERLINK("${String(url).replace(/"/g, '""')}","${label.replace(/"/g, '""')}")`;
        };
        const maxImageCount = this.state.rows.reduce((maxCount: number, row: any) => {
            const imageCount = Array.isArray(row.images) ? row.images.length : 0;
            return Math.max(maxCount, imageCount);
        }, 0);
        const imageHeaders = Array.from({ length: maxImageCount }, (_, index) => `Image Link ${index + 1}`);
        const headers = ['Priority','City','Date of Pickup','Barcode','User Id','Community Name','H. No','User Name','Notes', ...imageHeaders];
        const csvRows = this.state.rows.map((row: any) => [
            row.priority, row.city,
            row.pickup_date ? row.pickup_date.split(' ')[0].split('T')[0] : '',
            row.barcode, row.user_id, row.community_name, row.house_number,
            row.user_name, row.notes,
            ...Array.from(
                { length: maxImageCount },
                (_, index) => {
                    if (!Array.isArray(row.images) || !row.images[index]) {
                        return '';
                    }
                    return buildImageHyperlink(row.images[index], row.barcode || '', index);
                }
            ),
        ].map(escape).join(','));
        const csv = [headers.map(escape).join(','), ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'Odd-Bag-Report.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    handleFetchReport = () => {
        this.setState({ showLoader: true });
        const payload: any = {
            from_date: this.state.fromDate,
            to_date: this.state.toDate,
            limit: this.state.pageSize,
            page: this.state.currentPage + 1,
        };

        if (this.state.community?.id) {
            payload.hierarchy_id = this.state.community.id;
        }

        axios.post('fetchAllOddBags', payload)
            .then((response) => {
                const paginatedData = response.data?.data || {};
                const rows = (paginatedData.data || []).map((row: any) => ({
                    id: row.id,
                    priority: row.status || '',
                    city: row.hierarchy_name || '',
                    pickup_date: row.pickup_date || '',
                    barcode: row.bag_barcode || '',
                    user_id: row.user_id || '',
                    community_name: row.community_name || '',
                    house_number: row.h_no || '',
                    user_name: row.user_name || row.created_by_user_name || '',
                    notes: row.notes || '',
                    images: row.images || [],
                }));

                this.setState({
                    rows,
                    rowCount: paginatedData.total || 0,
                    showLoader: false,
                });
            })
            .catch((error) => {
                console.error('Error fetching odd bag report:', error);
                this.setState({ showLoader: false, rows: [], rowCount: 0 });
            });
    };

    public render() {
        if (!localStorage.getItem('token')) {
            return <Redirect to="/login" />;
        }

        const { t } = this.props;

        const getImageName = (image_url: string) => {
            let name = image_url.split('/');
            return name[name.length - 1];
        };

        const formatPickupDate = (value: string) => {
            if (!value) {
                return '';
            }

            return value.split(' ')[0].split('T')[0];
        };

        const downloadImage = async (imageUrl: string) => {
            try {
                const downloadLink = document.createElement('a');
                downloadLink.href = imageUrl;
                downloadLink.setAttribute('download', getImageName(imageUrl));
                downloadLink.setAttribute('target', '_blank');
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            } catch (error) {
                console.error('Error downloading image:', error);
            }
        };

        const renderMaskedCell = (params: GridCellParams) => (
            <Box
                component='span'
                sx={isUatEnvironment ? {
                    filter: 'blur(4px)',
                    opacity: 0.5,
                    userSelect: 'none',
                } : undefined}
            >
                {params.value}
            </Box>
        );

        const columns: any[] = [
            { headerName: 'ID', field: 'id', hide: true },
            { headerName: 'Priority', field: 'priority', width: 90 },
            { headerName: 'City', field: 'city', width: 130 },
            {
                headerName: 'Date of Pickup',
                field: 'pickup_date',
                width: 130,
                renderCell: (params: GridCellParams) => formatPickupDate(params.row.pickup_date),
            },
            { headerName: 'Barcode', field: 'barcode', width: 130 },
            { headerName: 'User Id', field: 'user_id', width: 90 },
            { headerName: 'Community Name', field: 'community_name', width: 180, renderCell: renderMaskedCell },
            { headerName: 'H. No', field: 'house_number', width: 100, renderCell: renderMaskedCell },
            { headerName: 'User Name', field: 'user_name', width: 150, renderCell: renderMaskedCell },
            { headerName: 'Notes', field: 'notes', width: 200 },
            {
                headerName: 'Image Link',
                field: 'images',
                width: 260,
                renderCell: (params: GridCellParams) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            gap: 0.5,
                            width: '100%',
                        }}
                    >
                        {Array.isArray(params.row.images) &&
                            params.row.images.map((image: string, imageKey: number) => (
                                <Button
                                    key={imageKey}
                                    variant='text'
                                    sx={{
                                        minWidth: 0,
                                        whiteSpace: 'nowrap',
                                        textTransform: 'none',
                                        p: 0,
                                    }}
                                    onClick={() =>
                                        this.setState({ showImageModal: true, currentImage: image })
                                    }
                                >
                                    {getImageName(image)}
                                </Button>
                            ))}
                    </Box>
                ),
            },
        ];

        return (
            <div>
                <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={this.state.showLoader}>
                    <CircularProgress className='customLoader' />
                </Backdrop>
                <ReportsHeader user={this.props.user} />
                <Grid container spacing={2}>
                    <Grid item md={12} xs={12} className='mt-3'>
                        <Card>
                            <CardHeader
                                title={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            flexWrap: 'nowrap',
                                            py: 1,
                                        }}
                                    >
                                        <Typography
                                            variant='h6'
                                            sx={{ fontWeight: 400, whiteSpace: 'nowrap', minWidth: 'max-content' }}
                                        >
                                            {t('Odd Bag Report')}
                                        </Typography>
                                        <Autocomplete
                                            id='city-filter-label'
                                            options={[...this.state.communities].sort(
                                                (a, b) => (b.type || '').localeCompare(a.type || '')
                                            )}
                                            groupBy={(community) => community.type || 'City'}
                                            getOptionLabel={(community) => community.name}
                                            renderInput={(params: any) => (
                                                <TextField {...params} label='City Filter' />
                                            )}
                                            onChange={(_e: any, value: any) =>
                                                this.setState({ community: value, currentPage: 0 }, () =>
                                                    this.handleFetchReport()
                                                )
                                            }
                                            size='small'
                                            sx={{ minWidth: 180, ml: 4 }}
                                        />
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                renderInput={(props) => (
                                                    <TextField {...props} size='small' sx={{ width: 170 }} />
                                                )}
                                                label='From Date'
                                                views={['year', 'month', 'day']}
                                                value={this.state.fromDate}
                                                onChange={(newValue: any) => {
                                                    this.setState(
                                                        { fromDate: formatDate(newValue, 'Y-m-d'), currentPage: 0 },
                                                        () => this.handleFetchReport()
                                                    );
                                                }}
                                                inputFormat='yyyy-MM-dd'
                                                mask='____-__-__'
                                            />
                                        </LocalizationProvider>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                renderInput={(props) => (
                                                    <TextField {...props} size='small' sx={{ width: 170 }} />
                                                )}
                                                label='To Date'
                                                views={['year', 'month', 'day']}
                                                value={this.state.toDate}
                                                onChange={(newValue: any) => {
                                                    this.setState(
                                                        { toDate: formatDate(newValue, 'Y-m-d'), currentPage: 0 },
                                                        () => this.handleFetchReport()
                                                    );
                                                }}
                                                inputFormat='yyyy-MM-dd'
                                                mask='____-__-__'
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                }
                                className='text-left'
                            />
                            <CardContent>
                                <CustomToolbar onExport={this.handleExportCsv} />
                                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                    <Box sx={{ minWidth: 1600 }}>
                                        <DataGrid
                                            aria-label='Odd Bag Report'
                                            columns={columns}
                                            getRowHeight={(params) => {
                                                const imageCount = Array.isArray(params.model.images) ? params.model.images.length : 0;
                                                return imageCount > 1 ? imageCount * 34 : 52;
                                            }}
                                            rows={this.state.rows}
                                            pageSize={this.state.pageSize}
                                            rowCount={this.state.rowCount}
                                            page={this.state.currentPage}
                                            onPageChange={(page) => this.setState({ currentPage: page }, () => this.handleFetchReport())}
                                            onPageSizeChange={(newPageSize) =>
                                                this.setState({ pageSize: newPageSize, currentPage: 0 }, () => this.handleFetchReport())
                                            }
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            autoHeight
                                            disableSelectionOnClick
                                            className='clickableList'
                                            sx={{
                                                '& .MuiDataGrid-virtualScroller': {
                                                    overflowX: 'hidden !important',
                                                },
                                            }}
                                            pagination
                                            paginationMode='server'
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Image Preview Modal */}
                <Modal
                    open={this.state.showImageModal}
                    onClose={() => this.setState({ showImageModal: false })}
                    aria-labelledby='image-modal-title'
                    aria-describedby='image-modal-description'
                >
                    <Box sx={style}>
                        <Card>
                            <CardHeader
                                title={getImageName(this.state.currentImage)}
                                className='text-left'
                                action={
                                    <IconButton
                                        color='primary'
                                        className='customIconButton'
                                        aria-label='close'
                                        component='span'
                                        onClick={() => this.setState({ showImageModal: false })}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                }
                            />
                            <CardContent style={{ height: '550px', overflowY: 'scroll' }}>
                                <Divider className='mb-3' />
                                <Grid container spacing={2}>
                                    <Grid item md={12} xs={12} className='text-center'>
                                        <img
                                            src={this.state.currentImage}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            width={600}
                                            height={450}
                                            alt=''
                                        />
                                    </Grid>
                                </Grid>
                                <Divider className='mt-3' />
                            </CardContent>
                            <CardActions className='float-right'>
                                <Button
                                    size='small'
                                    className='customCloseButton'
                                    onClick={() => this.setState({ showImageModal: false })}
                                >
                                    Close
                                </Button>
                                <Button
                                    size='small'
                                    variant='contained'
                                    sx={{ outline: 'none !important' }}
                                    onClick={() => downloadImage(this.state.currentImage)}
                                >
                                    Download
                                </Button>
                            </CardActions>
                        </Card>
                    </Box>
                </Modal>
            </div>
        );
    }
}

export default withRouter(withTranslation()(OddBagReport));
